
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { generateClient } from 'aws-amplify/api';
import { useAuth } from './AuthContext';
import { chatApi } from '../services/chatApi';
import { typingService } from '../services/typingService';
import { presenceService } from '../services/presenceService';
import { notificationService } from '../services/notificationService';
import { groupLockService } from '../services/groupLockService';
import { subscribeToMessages } from '../services/subscriptions';
import eventService from '../services/eventService';
import { chatUtils } from '../utils/chatUtils';

// Lazy client initialization to ensure Amplify is configured first
let client = null;
const getClient = () => {
  if (!client) {
    client = generateClient();
  }
  return client;
};

const ChatContext = createContext();

const conversationSubscription = /* GraphQL */ `
  subscription OnUpdateConversation {
    onUpdateConversation {
      id
      user1ID
      user2ID
      isGroup
      groupName
      lastMessageText
      lastMessageAt
      user1UnreadCount
      user2UnreadCount
      updatedAt
    }
  }
`;

const newConversationSubscription = /* GraphQL */ `
  subscription OnCreateConversation {
    onCreateConversation {
      id
      user1ID
      user2ID
      isGroup
      groupName
      lastMessageText
      lastMessageAt
      user1UnreadCount
      user2UnreadCount
      createdAt
      updatedAt
    }
  }
`;

const messageSubscription = /* GraphQL */ `
  subscription OnCreateMessage {
    onCreateMessage {
      id
      conversationID
      senderID
      receiverID
      content
      messageType
      isRead
      createdAt
    }
  }
`;

// --- Normalization Helper ---
const normalizeConversation = (conv, currentUserId) => {
  if (!conv || !conv.id) return null;

  const normalized = {
    id: conv.id,
    isGroup: conv.isGroup || false,
    groupName: conv.groupName,
    lastMessageText: conv.lastMessageText || null,
    lastMessageAt: conv.lastMessageAt || conv.updatedAt || conv.createdAt,
    raw: conv,
    user1ID: conv.user1ID,
    user2ID: conv.user2ID,
    createdBy: conv.createdBy
  };

  if (conv.isGroup) {
    normalized.memberCount = conv.participants?.items?.length || 1;
    normalized.otherUser = null;
    normalized.groupName = conv.groupName || 'Group Chat';
    normalized.unreadCount = 0;
  } else {
    // For direct conversations, determine other user
    const otherUser = conv.user1ID === currentUserId ? conv.user2 : conv.user1;
    normalized.otherUser = otherUser || {
      id: conv.user1ID === currentUserId ? conv.user2ID : conv.user1ID,
      username: 'Unknown User',
      displayName: 'Unknown User'
    };
    normalized.unreadCount = conv.user1ID === currentUserId ? 
      (conv.user1UnreadCount || 0) : (conv.user2UnreadCount || 0);
  }

  return normalized;
};


export const ChatProvider = ({ children }) => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [conversations, setConversations] = useState([]);
  const [typingUsers, setTypingUsers] = useState({});
  const [loading, setLoading] = useState(true);


  const fetchConversations = useCallback(async (userId) => {
    setLoading(true);
    try {
      // Cost optimization: Limit to 50 most recent conversations
      const conversations = await chatApi.getConversations(userId, 50);
      const normalized = conversations
        .filter(c => c && c.id) // Filter out null/invalid conversations
        .map(c => normalizeConversation(c, userId))
        .filter(Boolean) // Filter out failed normalizations

        .sort((a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt)); // Sort by recency
      setConversations(normalized);
    } catch (error) {
      console.error('Error loading conversations:', error);
      setConversations([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.id) {
      fetchConversations(user.id).catch(err => {
        console.error('Failed to fetch conversations:', err);
        setConversations([]);
        setLoading(false);
      });
      
      // Start presence tracking (fallback mode)
      try {
        presenceService.startTracking(user.id);
      } catch (error) {
        console.log('Presence tracking disabled:', error.message);
      }
      
      // Request notification permission (silently)
      try {
        notificationService.requestPermission();
      } catch (error) {
        console.log('Notifications disabled:', error.message);
      }
      
      // Start group lock monitoring
      groupLockService.startMonitoring();
    } else {
      setConversations([]);
      setLoading(false);
    }
    
    return () => {
      if (user?.id) {
        presenceService.stopTracking(user.id);
      }
      groupLockService.stopMonitoring();
    };
  }, [user?.id, fetchConversations]);

  // --- Subscription for Conversation Updates (Cost Optimized) ---
  useEffect(() => {
    if (!user?.id) return;

    // Cost optimization: Debounce subscription updates
    let updateTimeout;
    const subscriptions = [];
    
    try {
      // Subscribe to conversation updates
      const updateSub = getClient().graphql({
        query: conversationSubscription
      }).subscribe({
        next: ({ data }) => {
          const updatedConv = data.onUpdateConversation;
          if (!updatedConv) return;

          const isParticipant = updatedConv.isGroup || updatedConv.user1ID === user.id || updatedConv.user2ID === user.id;
          if (!isParticipant) return;

          // Debounce updates to reduce state changes
          clearTimeout(updateTimeout);
          updateTimeout = setTimeout(() => {
            setConversations(prev => {
              const existingIndex = prev.findIndex(conv => conv.id === updatedConv.id);
              if (existingIndex >= 0) {
                const updated = [...prev];
                updated[existingIndex] = {
                  ...updated[existingIndex],
                  lastMessageText: updatedConv.lastMessageText,
                  lastMessageAt: updatedConv.lastMessageAt,
                  user1UnreadCount: updatedConv.user1UnreadCount,
                  user2UnreadCount: updatedConv.user2UnreadCount,
                  unreadCount: updatedConv.user1ID === user.id ? 
                    (updatedConv.user1UnreadCount || 0) : (updatedConv.user2UnreadCount || 0)
                };
                // Only sort if order actually changed
                if (existingIndex > 0) {
                  return updated.sort((a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt));
                }
                return updated;
              }
              return prev;
            });
          }, 500); // 500ms debounce
        },
        error: (err) => {
          console.warn('Conversation subscription error (non-blocking):', err.message);
        }
      });
      subscriptions.push(updateSub);

      // Subscribe to new conversations
      const createSub = getClient().graphql({
        query: newConversationSubscription
      }).subscribe({
        next: ({ data }) => {
          const newConv = data.onCreateConversation;
          if (!newConv) return;

          const isParticipant = newConv.isGroup || newConv.user1ID === user.id || newConv.user2ID === user.id;
          if (!isParticipant) return;

          console.log('🆕 New conversation detected:', newConv.id);
          
          // Add new conversation to list if not already present
          setConversations(prev => {
            const exists = prev.find(conv => conv.id === newConv.id);
            if (exists) return prev;
            
            const normalized = normalizeConversation(newConv, user.id);
            if (!normalized) return prev;
            
            const updated = [normalized, ...prev];
            return updated.sort((a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt));
          });
        },
        error: (err) => {
          console.warn('New conversation subscription error (non-blocking):', err.message);
        }
      });
      subscriptions.push(createSub);

      return () => {
        clearTimeout(updateTimeout);
        subscriptions.forEach(sub => sub.unsubscribe());
      };
    } catch (error) {
      console.warn('Conversation subscription initialization failed (non-blocking):', error.message);
      // Return empty cleanup function - app continues without subscriptions
      return () => {};
    }
  }, [user?.id]);

  // --- Event API Subscription for New Messages ---
  useEffect(() => {
    if (!user?.id || conversations.length === 0) return;

    // Cost optimization: Limit active subscriptions to top 10 conversations
    const activeConversations = conversations.slice(0, 10);
    const conversationIds = activeConversations.map(c => c.id).sort().join(',');
    const subscriptions = [];

    // Subscribe to each active conversation with error handling
    activeConversations.forEach(conversation => {
      const handleNewMessage = (newMessage) => {
        if (!newMessage || !newMessage.content || newMessage.content.startsWith('__TYPING__')) return;

        // Update unread count for messages from others
        if (newMessage.senderID !== user.id) {
          let senderName = 'Someone';
          
          if (conversation.isGroup) {
            senderName = newMessage.sender?.displayName || newMessage.sender?.username || 'Someone';
          } else {
            senderName = conversation.otherUser?.displayName || conversation.otherUser?.username || 'Someone';
          }
          
          // Show notification if app is not focused (cost optimization: throttle notifications)
          if (!document.hasFocus() && Math.random() < 0.8) { // 80% chance to reduce notification API calls
            try {
              if (notificationService && typeof notificationService.showMessageNotification === 'function') {
                notificationService.showMessageNotification(
                  newMessage.content,
                  senderName,
                  newMessage.conversationID
                );
              }
            } catch (error) {
              console.error('Error showing message notification:', error);
            }
          }
        }

        // Update conversation last message and unread count immediately
        setConversations(prev => {
          const existingIndex = prev.findIndex(conv => conv.id === newMessage.conversationID);
          if (existingIndex >= 0) {
            const currentConv = prev[existingIndex];
            const updatedConv = {
              ...currentConv,
              lastMessageText: newMessage.content,
              lastMessageAt: newMessage.createdAt,
              // Increment unread count if message is from someone else
              unreadCount: newMessage.senderID !== user.id ? 
                (currentConv.unreadCount || 0) + 1 : currentConv.unreadCount
            };
            
            // Create new array with updated conversation
            const updated = [...prev];
            updated[existingIndex] = updatedConv;
            
            // Only reorder if this conversation should be at top and isn't already
            if (existingIndex > 0) {
              const shouldMoveToTop = new Date(updatedConv.lastMessageAt) >= new Date(prev[0].lastMessageAt);
              if (shouldMoveToTop) {
                updated.splice(existingIndex, 1);
                updated.unshift(updatedConv);
              }
            }
            
            return updated;
          }
          return prev;
        });
      };

      try {
        const subscription = eventService.subscribeToChat(conversation.id, handleNewMessage);
        subscriptions.push({ conversationId: conversation.id, subscription });
      } catch (error) {
        console.warn(`Failed to subscribe to chat ${conversation.id}:`, error.message);
        // Continue without this subscription - app remains functional
      }
    });

    return () => {
      subscriptions.forEach(({ conversationId }) => {
        eventService.unsubscribe(`chat_${conversationId}`);
      });
    };
  }, [user?.id, conversations.map(c => c.id).sort().join(',')]);

  // --- Recalculate total unread count ---
  useEffect(() => {
    const total = conversations.reduce((sum, conv) => sum + (conv.unreadCount || 0), 0);
    setUnreadCount(total);
  }, [conversations]);

  const markAsRead = useCallback(async (conversationId) => {
    if (!user?.id) return;

    // Immediately update UI to show as read
    setConversations(prev => prev.map(conv => 
      conv.id === conversationId ? { ...conv, unreadCount: 0 } : conv
    ));
    
    try {
      const result = await chatApi.markConversationAsRead(conversationId, user.id);
      console.log('✅ Mark as read successful:', result);
    } catch (error) {
      console.error('❌ Error marking conversation as read:', error);
      // Don't revert UI state - keep it as read for better UX
      // The backend error is logged but doesn't affect user experience
    }
  }, [user?.id]);

  const updateConversationLastMessage = useCallback((conversationId, lastMessage) => {
    setConversations(prev => prev.map(conv => 
      conv.id === conversationId ? {
        ...conv,
        lastMessageText: lastMessage.text || lastMessage.content,
        lastMessageAt: lastMessage.timestamp || lastMessage.createdAt || new Date().toISOString()
      } : conv
    ));
  }, []);

  const sendMessage = useCallback(async (conversationID, receiverID, content, messageType = 'text') => {
    if (!user?.id) return null;
    
    try {
      return await eventService.sendMessage(conversationID, user.id, receiverID, content, messageType);
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }, [user?.id]);

  return (
    <ChatContext.Provider value={{
      unreadCount,
      conversations,
      loading,
      markAsRead,
      refreshConversations: useCallback(() => {
        if (user?.id) {
          fetchConversations(user.id);
        }
      }, [user?.id, fetchConversations]),
      updateConversationLastMessage,
      sendMessage,
      typingUsers,
    }}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within ChatProvider');
  }
  return context;
};
